// Global Instructions Rule Applied!

import React, { useCallback, useMemo, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { Button, Input, Popconfirm, Space, Table, Tag, Tooltip, Typography } from 'antd'
import { PlusOutlined, EditOutlined, DeleteOutlined, ArrowLeftOutlined } from '@ant-design/icons'
import { removeTab } from '../../../store/slices/tabSlice'
import type { ColumnsType } from 'antd/es/table'
import { toast } from 'react-toastify'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import { store } from '../../../state_manager/store.ts'
import { getAccessToken } from '../../../core/auth/authService'
import type { CommsTopicGridModel } from '../../../state_manager/data_models/comms_topic_grid_model.ts'
import { useTopicsData } from '../hooks/useTopicsData.ts'
import { createTopic, updateTopic, deleteTopic } from '../services/distributionListsService.ts'
import { TopicModal } from './TopicModal.tsx'

interface AccountRoleChoice {
  id: string
  role: string
}

const { Title } = Typography

export default function TopicManagementPage() {
  const { topicsData, topicsDataLoading, activeSorting, handleSetFilters, handleSetSorting, refetch } =
    useTopicsData()

  // Topic Management has no left-nav item (§2.15h). The back arrow closes
  // this tab and returns to Topic Subscriptions (where the "Manage Topics"
  // button opened it). 'topics-table' is the tabSlice.defaultTabs['topics']
  // key.
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const goBackToSubscriptions = useCallback(() => {
    navigate('/topic-subscriptions')
    dispatch(removeTab({ key: 'topics-table' }))
  }, [navigate, dispatch])

  const accountRoleChoices = useSelector(
    (state: any) => (state.globalConfigs?.account_role_choices ?? []) as AccountRoleChoice[]
  )

  const [modalOpen, setModalOpen] = useState(false)
  const [editingTopic, setEditingTopic] = useState<CommsTopicGridModel | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [deletingId, setDeletingId] = useState<string | null>(null)

  const currentSort = activeSorting?.[0]

  const openCreate = useCallback(() => {
    setEditingTopic(null)
    setModalOpen(true)
  }, [])

  const openEdit = useCallback((topic: CommsTopicGridModel) => {
    setEditingTopic(topic)
    setModalOpen(true)
  }, [])

  const handleSubmit = useCallback(
    async (label: string, roleIds: string[]) => {
      setSubmitting(true)
      try {
        const token = getAccessToken(store.getState().auth)
        const result = editingTopic
          ? await updateTopic(editingTopic.comms_topics__id, label, roleIds, token)
          : await createTopic(label, roleIds, token)

        if (result.error || !result.data) {
          showErrorToast(result.error || 'Failed to save topic')
          return
        }
        toast.success(editingTopic ? 'Topic updated' : 'Topic created')
        setModalOpen(false)
        setEditingTopic(null)
        // Refetch through the data RPC so the grid reflects DB truth
        // (re-aggregated roles), not an optimistic guess.
        await refetch()
      } finally {
        setSubmitting(false)
      }
    },
    [editingTopic, refetch]
  )

  const handleDelete = useCallback(
    async (topic: CommsTopicGridModel) => {
      setDeletingId(topic.comms_topics__id)
      try {
        const token = getAccessToken(store.getState().auth)
        const result = await deleteTopic(topic.comms_topics__id, token)
        if (!result.success) {
          showErrorToast(result.error || 'Failed to delete topic')
          return
        }
        toast.success('Topic deleted')
        await refetch()
      } finally {
        setDeletingId(null)
      }
    },
    [refetch]
  )

  const columns: ColumnsType<CommsTopicGridModel> = useMemo(
    () => [
      {
        title: 'Topic',
        dataIndex: 'comms_topics__label',
        key: 'comms_topics__label',
        sorter: true,
        sortOrder:
          currentSort?.key === 'comms_topics__label'
            ? currentSort.direction === 'desc'
              ? 'descend'
              : 'ascend'
            : null,
        render: (label: string) => <span className='font-medium'>{label}</span>
      },
      {
        title: 'Account Roles',
        dataIndex: 'comms_topics__role_ids',
        key: 'comms_topics__role_ids',
        render: (_: unknown, record: CommsTopicGridModel) => {
          const labels = (record.comms_topics__role_labels || '')
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean)
          if (labels.length === 0) return <span style={{ color: '#9ca3af' }}>—</span>
          return (
            <Space size={[4, 4]} wrap>
              {labels.map((l) => (
                <Tag key={l} color='blue'>
                  {l}
                </Tag>
              ))}
            </Space>
          )
        }
      },
      {
        title: 'No. Roles',
        dataIndex: 'comms_topics__role_count',
        key: 'comms_topics__role_count',
        width: 110,
        align: 'right',
        sorter: true,
        sortOrder:
          currentSort?.key === 'comms_topics__role_count'
            ? currentSort.direction === 'desc'
              ? 'descend'
              : 'ascend'
            : null
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 120,
        align: 'center',
        render: (_: unknown, record: CommsTopicGridModel) => (
          <Space size='small'>
            <Tooltip title='Edit'>
              <Button
                type='text'
                size='small'
                icon={<EditOutlined />}
                onClick={() => openEdit(record)}
              />
            </Tooltip>
            <Popconfirm
              title='Delete this topic?'
              description='Its account-role mappings are removed too.'
              okText='Delete'
              okButtonProps={{ danger: true }}
              onConfirm={() => handleDelete(record)}
            >
              <Tooltip title='Delete'>
                <Button
                  type='text'
                  size='small'
                  danger
                  loading={deletingId === record.comms_topics__id}
                  icon={<DeleteOutlined />}
                />
              </Tooltip>
            </Popconfirm>
          </Space>
        )
      }
    ],
    [currentSort, openEdit, handleDelete, deletingId]
  )

  const handleTableChange = useCallback(
    (_pagination: unknown, _filters: unknown, sorter: any) => {
      const s = Array.isArray(sorter) ? sorter[0] : sorter
      if (s && s.order && s.columnKey) {
        handleSetSorting([{ key: String(s.columnKey), direction: s.order === 'descend' ? 'desc' : 'asc' }])
      } else {
        handleSetSorting([{ key: 'comms_topics__label', direction: 'asc' }])
      }
    },
    [handleSetSorting]
  )

  const onSearch = useCallback(
    (value: string) => {
      const v = (value || '').trim()
      handleSetFilters(v ? [{ key: 'comms_topics__label', value: v }] : [])
    },
    [handleSetFilters]
  )

  return (
    <div className='h-full overflow-hidden'>
      <div className='w-full h-full flex flex-col bg-white px-2 sm:px-4 py-2 sm:py-4 overflow-hidden'>
        <div className='flex items-center justify-between mb-3 gap-3 flex-wrap'>
          <Space>
            <Tooltip title='Back to Topic Subscriptions'>
              <Button
                aria-label='Back to Topic Subscriptions'
                icon={<ArrowLeftOutlined />}
                onClick={goBackToSubscriptions}
              />
            </Tooltip>
            <Title level={4} style={{ margin: 0 }}>
              Topic Management
            </Title>
          </Space>
          <Space>
            <Input.Search
              placeholder='Search topics...'
              allowClear
              onSearch={onSearch}
              style={{ width: 240 }}
            />
            <Button type='primary' icon={<PlusOutlined />} onClick={openCreate}>
              New Topic
            </Button>
          </Space>
        </div>

        <div className='flex-1 min-h-0 overflow-auto'>
          <Table<CommsTopicGridModel>
            rowKey='comms_topics__id'
            columns={columns}
            dataSource={topicsData}
            loading={topicsDataLoading}
            onChange={handleTableChange}
            pagination={{ pageSize: 50, hideOnSinglePage: true, showSizeChanger: false }}
            size='middle'
          />
        </div>

        <TopicModal
          open={modalOpen}
          topic={editingTopic}
          accountRoleChoices={accountRoleChoices}
          confirmLoading={submitting}
          onSubmit={handleSubmit}
          onCancel={() => {
            setModalOpen(false)
            setEditingTopic(null)
          }}
        />
      </div>
    </div>
  )
}
